/**
 * Utilities for detecting interactive elements and handling user interactions
 */

/**
 * Interactive HTML element selectors that should not trigger bookmark selection
 */
const INTERACTIVE_SELECTORS = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]',
  '[role="button"]',
  '[role="link"]',
  '[contenteditable]',
  '.bookmark-actions', // Bookmark actions container
].join(', ');

/**
 * Check if an element or its ancestors are interactive elements that should not trigger selection
 * @param element - The element to check
 * @param container - Optional container element to stop searching at (won't match the container itself)
 * @returns true if the element is interactive and should not trigger selection
 */
export function isInteractiveElement(element: Element, container?: Element): boolean {
  // Check if element itself matches interactive selectors
  const closestInteractive = element.closest(INTERACTIVE_SELECTORS);

  // If we found an interactive element, make sure it's not the container itself
  if (closestInteractive && closestInteractive !== container) {
    return true;
  }

  // Check for specific data attributes that indicate interactive elements
  const interactiveDataAttrs = ['data-clickable', 'data-interactive'];
  for (const attr of interactiveDataAttrs) {
    const interactiveAttrElement = element.closest(`[${attr}]`);
    if (interactiveAttrElement && interactiveAttrElement !== container) {
      return true;
    }
  }

  return false;
}

/**
 * Check if a keyboard event should trigger bookmark selection
 * @param event - The keyboard event
 * @param target - The target element
 * @param container - Optional container element to exclude from interactive check
 * @returns true if the event should trigger selection
 */
export function shouldHandleKeyboardSelection(
  event: KeyboardEvent,
  target: Element,
  container?: Element,
): boolean {
  // Only handle Space and Enter keys
  if (event.key !== ' ' && event.key !== 'Enter') {
    return false;
  }

  // Don't handle if target is already an interactive element
  if (isInteractiveElement(target, container)) {
    return false;
  }

  // Don't handle if any modifier keys are pressed
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
    return false;
  }

  return true;
}
